/**
 * Motor de áudio da mesa: toca a música e os efeitos enquanto a pessoa estiver
 * na sala, com ou sem os módulos de música/efeitos abertos (eles são só a
 * interface).
 *
 * A música segue o estado do servidor: a posição esperada é
 * `audioPositionSeconds` (posição gravada + tempo decorrido) e, quando o desvio
 * passa da tolerância, o `<audio>` salta para o lugar certo. Quando a faixa
 * acaba, quem administra a mesa avisa o servidor, que passa para a próxima (o
 * servidor ignora avisos repetidos).
 *
 * Volume final = geral × (música ou efeitos) × volume da mesa (do mestre).
 */
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { api } from '../../api.js';
import { audioPositionSeconds, shouldCorrectDrift } from '../../shared.js';
import { serverNow } from '../../serverClock.js';
import { useApp } from '../../store.js';
import { serverUrl } from '../../serverUrl.js';
import { translate, useLanguage } from '../../i18n/index.js';

export type Playback = {
  position: number;
  duration: number;
  /** o navegador bloqueou o som até a pessoa clicar em algo */
  blocked: boolean;
  /** o link da faixa não abriu */
  failed: boolean;
};

export const usePlayback = create<Playback>()(() => ({ position: 0, duration: 0, blocked: false, failed: false }));
// para os testes ponta a ponta
if (import.meta.env.DEV) (globalThis as { __rpgDeskPlayback?: unknown }).__rpgDeskPlayback = usePlayback;

const urlCache = new Map<string, { url: string; at: number }>();

/** `file:<id>` vira o link assinado do arquivo; http(s) passa direto (um caminho do servidor, no endereço dele). */
export async function resolveAudioUrl(url: string): Promise<string> {
  if (!url.startsWith('file:')) return serverUrl(url);
  const id = url.slice(5);
  const hit = urlCache.get(id);
  if (hit && Date.now() - hit.at < 30 * 60_000) return hit.url;
  const token = useApp.getState().token;
  if (!token) throw new Error(translate(useLanguage.getState().language, 'error.noSession'));
  const { url: signed } = await api.fileUrl(token, id);
  urlCache.set(id, { url: signed, at: Date.now() });
  return signed;
}

/** Põe o `<audio>` da música no lugar e no estado (tocando/pausado) que o servidor diz. */
function syncMusic(el: HTMLAudioElement): void {
  const state = useApp.getState().audio;
  if (!state?.current) return;
  const expected = audioPositionSeconds(state, serverNow());
  if (shouldCorrectDrift(expected, el.currentTime)) {
    try {
      el.currentTime = expected;
    } catch {
      // metadados ainda não chegaram: a correção periódica resolve
    }
  }
  if (state.isPlaying) {
    if (el.paused) {
      void el.play().then(
        () => usePlayback.setState({ blocked: false }),
        (error: unknown) => {
          if (error instanceof DOMException && error.name === 'NotAllowedError') usePlayback.setState({ blocked: true });
        },
      );
    }
  } else if (!el.paused) el.pause();
}

let unlockMusic: (() => void) | null = null;
/** Libera o som depois de um clique (política de autoplay do navegador). */
export function unlockAudio(): void {
  unlockMusic?.();
}

export function AudioEngine() {
  const audio = useApp((s) => s.audio);
  const settings = useApp((s) => s.settings);
  const sfxNonce = useApp((s) => s.sfxNonce);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const loaded = useRef<{ key: string; ready: boolean }>({ key: '', ready: false });
  const roomVolume = audio?.volume ?? 1;
  const base = settings.audioMuted ? 0 : settings.masterVolume * roomVolume;
  const musicVolume = Math.min(1, Math.max(0, base * settings.musicVolume));

  // um <audio> só para a música, criado uma vez
  useEffect(() => {
    const el = new Audio();
    el.preload = 'auto';
    musicRef.current = el;
    const onTime = () => usePlayback.setState({ position: el.currentTime });
    const onMeta = () => usePlayback.setState({ duration: Number.isFinite(el.duration) ? el.duration : 0 });
    const onError = () => {
      if (el.getAttribute('src')) usePlayback.setState({ failed: true });
    };
    const onEnded = () => {
      const { audio: state, room, user, members, controlAudio } = useApp.getState();
      if (!state?.current || !room || !user) return;
      const admin = room.ownerId === user.id || !!members[user.id]?.modes.includes('gm');
      if (admin) controlAudio('ended', { trackId: state.current.id });
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('error', onError);
    el.addEventListener('ended', onEnded);
    unlockMusic = () => {
      const state = useApp.getState().audio;
      if (state?.isPlaying && el.paused) {
        void el.play().then(
          () => usePlayback.setState({ blocked: false }),
          () => undefined,
        );
      } else usePlayback.setState({ blocked: false });
    };
    // qualquer clique na página destrava o som bloqueado
    const onGesture = () => {
      if (usePlayback.getState().blocked) unlockMusic?.();
    };
    window.addEventListener('pointerdown', onGesture, true);
    window.addEventListener('keydown', onGesture, true);
    return () => {
      window.removeEventListener('pointerdown', onGesture, true);
      window.removeEventListener('keydown', onGesture, true);
      el.pause();
      el.removeAttribute('src');
      el.load();
      unlockMusic = null;
      usePlayback.setState({ position: 0, duration: 0, blocked: false, failed: false });
    };
  }, []);

  // acompanha o estado do servidor
  useEffect(() => {
    const el = musicRef.current;
    if (!el) return;
    el.volume = musicVolume;
    const current = audio?.current ?? null;
    const key = current ? `${current.id}|${current.url}` : '';
    if (key === loaded.current.key) {
      if (loaded.current.ready) syncMusic(el);
      return;
    }
    // faixa nova: o link é resolvido uma vez por faixa (o estado pode mudar várias
    // vezes enquanto isso, e a resposta vale enquanto a faixa for a mesma)
    loaded.current = { key, ready: false };
    el.pause();
    usePlayback.setState({ position: 0, duration: 0, failed: false });
    if (!current) {
      el.removeAttribute('src');
      el.load();
      return;
    }
    resolveAudioUrl(current.url).then(
      (src) => {
        if (loaded.current.key !== key) return;
        el.src = src;
        loaded.current.ready = true;
        syncMusic(el);
      },
      () => loaded.current.key === key && usePlayback.setState({ failed: true }),
    );
  }, [audio, musicVolume]);

  // correção periódica do desvio (o relógio do navegador escorrega)
  useEffect(() => {
    const timer = window.setInterval(() => {
      const el = musicRef.current;
      const state = useApp.getState().audio;
      if (!el || !state?.current || !state.isPlaying || !loaded.current.ready || el.paused) return;
      const expected = audioPositionSeconds(state, serverNow());
      if (shouldCorrectDrift(expected, el.currentTime)) {
        try {
          el.currentTime = expected;
        } catch {
          // tenta de novo no próximo ciclo
        }
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, []);

  // efeitos: cada evento toca uma vez
  const lastNonce = useRef(sfxNonce);
  useEffect(() => {
    if (sfxNonce === lastNonce.current) return;
    lastNonce.current = sfxNonce;
    const event = useApp.getState().lastSfx;
    if (!event) return;
    const { settings: prefs, audio: state } = useApp.getState();
    const volume = prefs.audioMuted ? 0 : prefs.masterVolume * (state?.volume ?? 1) * prefs.sfxVolume;
    if (volume <= 0) return;
    resolveAudioUrl(event.url).then(
      (src) => {
        const el = new Audio(src);
        el.volume = Math.min(1, Math.max(0, volume));
        void el.play().catch(() => usePlayback.setState({ blocked: true }));
      },
      () => undefined,
    );
  }, [sfxNonce]);

  return null;
}
